import type { RelatedScheduleResponse, ScoreboardResponse } from '@on3/api';
import { useSite } from '@on3/ui-lib/index';
import { adjustColorForReadability } from '@on3/ui-lib/utils/accessibility/color';
import { getUserTimeZone } from '@on3/ui-lib/utils/date';
import { slugify } from '@on3/ui-lib/utils/slugify';
import { TextLink } from '@public/components/UI/Link/TextLink';
import { MasseyRatings } from '@public/components/UI/MasseyRatings/MasseyRatings';
import { isBefore, startOfDay } from 'date-fns';
import { useMemo } from 'react';

import { ScheduleItem } from '../ScheduleItem/ScheduleItem';
import styles from './HighSchoolSchedule.module.scss';

interface IScheduleModuleProps {
  full?: boolean;
  schedule?: ScoreboardResponse[];
  relatedModel?: RelatedScheduleResponse;
}

const VISIBLE_GAMES = 5;
const VISIBLE_PAST_GAMES = 2;
const VISIBLE_FUTURE_GAMES = 3;

export const HighSchoolSchedule = ({
  full,
  schedule,
  relatedModel,
}: IScheduleModuleProps) => {
  const { sport, year, organization } = relatedModel || {};

  const {
    defaultYears: { footballSeasonYear },
  } = useSite();

  const userTimeZone = getUserTimeZone();
  const isCurrentYear = year === footballSeasonYear;
  const sluggedSport = slugify(sport?.name || 'football');
  const viewAllLink = `/high-school/${organization?.urlSlug}/${sluggedSport}/schedule/`;

  const title = full
    ? `${organization?.name} ${
        !isCurrentYear ? year : ''
      } ${sport?.name} Schedule`.trim()
    : `${year} ${sport?.name} Schedule`;
  const showViewAllLink = !full && schedule && schedule.length > VISIBLE_GAMES;

  const list = useMemo(() => {
    // If full, return the entire schedule
    if (full) return schedule;

    // Otherwise, return a mix of past and future games
    const today = startOfDay(new Date());
    const pastGames: ScoreboardResponse[] = [];
    const futureGames: ScoreboardResponse[] = [];

    schedule?.forEach((game) => {
      const gameDate = new Date((game?.startDateUtc || 0) * 1000);

      if (isBefore(gameDate, today)) {
        pastGames.push(game);
      } else {
        futureGames.push(game);
      }
    });

    if (pastGames.length < VISIBLE_PAST_GAMES) {
      const sliceNumber = VISIBLE_GAMES - pastGames.length;

      return [...pastGames, ...futureGames.slice(0, sliceNumber)];
    } else if (futureGames.length < VISIBLE_FUTURE_GAMES) {
      const sliceNumber = VISIBLE_GAMES - futureGames.length;

      return [...pastGames.slice(-sliceNumber), ...futureGames];
    }

    return [
      ...pastGames.slice(-VISIBLE_PAST_GAMES),
      ...futureGames.slice(0, VISIBLE_FUTURE_GAMES),
    ];
  }, [full, schedule]);

  return (
    <section className={styles.block}>
      {!full && (
        <header>
          <h2 className={styles.heading}>{title}</h2>
          <MasseyRatings />
        </header>
      )}

      <div className={styles.schedule}>
        <table>
          <thead>
            <tr>
              <th id="date">Date</th>
              <th id="opponent">Opponent</th>
              <th id="score">Score</th>
              <th id="prediction">Prediction</th>
              <th id="winProbability">Win Probability</th>
            </tr>
          </thead>
          <tbody>
            {list?.map((game, i) => (
              <ScheduleItem
                game={game}
                key={game.key}
                relatedModel={relatedModel}
              />
            ))}
          </tbody>
        </table>
      </div>

      <p className={styles.disclaimer}>All times are in {userTimeZone} Time</p>

      {showViewAllLink && (
        <nav>
          <TextLink
            className={styles.btnViewAll}
            href={viewAllLink}
            style={{
              color: adjustColorForReadability(
                relatedModel?.organization?.primaryColor || '#fe3b1f',
              ),
            }}
          >
            View Full Schedule
          </TextLink>
        </nav>
      )}
    </section>
  );
};
